import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { DeploymentMonitor } from "./DeploymentMonitor";
import { deploymentConfig } from "./deploymentConfig";
import { probeDeployment } from "./deploymentService";
import type { DeploymentPhase } from "./types";

export interface UseDeploymentResult {
  phase: DeploymentPhase;
  countdownSeconds: number;
  requestReload: () => void;
}

/**
 * React adapter for {@link DeploymentMonitor}.
 * Wires router location and browser visibility events to the orchestrator.
 */
export function useDeployment(
  config = deploymentConfig,
): UseDeploymentResult {
  const { pathname } = useLocation();

  const [phase, setPhase] = useState<DeploymentPhase>("monitoring");
  const [countdownSeconds, setCountdownSeconds] = useState(
    config.countdownSeconds,
  );

  const monitorRef = useRef<DeploymentMonitor | null>(null);

  if (monitorRef.current === null) {
    monitorRef.current = new DeploymentMonitor({
      config,
      delegate: {
        onPhaseChange: setPhase,
        onCountdownTick: setCountdownSeconds,
      },
      probe: (signal) => probeDeployment(signal, config.checkUrl),
    });
  }

  const requestReload = useCallback(() => {
    monitorRef.current?.requestReload();
  }, []);

  // --- Trigger: application startup (once) ---
  useEffect(() => {
    if (!config.enabled) {
      return;
    }

    const monitor = monitorRef.current;
    void monitor?.startup();

    const remoteUnsubscribe = config.syncAdapter?.onRemoteUpdateDetected?.(
      () => {
        monitor?.forceUpdateDetected();
      },
    );

    return () => {
      remoteUnsubscribe?.();
      monitor?.destroy();
    };
  }, [config.enabled, config.syncAdapter]);

  // --- Trigger: route change (debounced via monitor) ---
  useEffect(() => {
    if (!config.enabled || !config.enableRouteChangeCheck) {
      return;
    }

    monitorRef.current?.onRouteChange(pathname);
  }, [config.enabled, config.enableRouteChangeCheck, pathname]);

  // --- Trigger: visibility + background polling ---
  useEffect(() => {
    if (!config.enabled) {
      return;
    }

    const monitor = monitorRef.current;

    const handleVisibilityChange = () => {
      monitor?.onVisibilityChange(document.visibilityState === "visible");
    };

    monitor?.onVisibilityChange(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      monitor?.onVisibilityChange(false);
    };
  }, [config.enabled]);

  return {
    phase,
    countdownSeconds,
    requestReload,
  };
}
